'use strict';
/*
  larsen — editorial paper style after jorgen.larsen.id. Warm cream "paper",
  high-contrast Didone serif, rust accent. Key word sits in a soft paper card
  (like the site's cards); the geometric butterfly key appears as a faint
  watermark and as a small signature mark. Calm, premium, human — the opposite
  of the punk/funky looks. Reads cleanly over footage via cream haloes + cards.
*/
const { layoutCue } = require('./_common');
const { roundRect, FONTS } = require('../lib/draw');

const SUP = '/System/Library/Fonts/Supplemental/';
const SERIF = [SUP + 'Didot.ttc', SUP + 'Bodoni 72.ttc', SUP + 'Georgia Bold.ttf', SUP + 'Times New Roman Bold.ttf'];

const palette = {
  bg: '#ece3d4',      // warm paper
  panel: '#f5f0e6',   // lighter card paper
  ink: '#1d1b16',     // near-black warm
  fg: '#1d1b16',
  accent: '#9e3f22',  // rust / terracotta (the site's label + link colour)
  muted: '#7c7464',
};

// The "butterfly key" from jorgen.larsen.id — two angular wings + a centre bar.
// Source viewBox 1200×1200; the mark spans y 320..880 (≈560 tall), centred (600,600).
function butterfly(ctx, cx, cy, h, color, alpha) {
  const s = h / 560;
  ctx.save();
  ctx.globalAlpha = (alpha == null ? 1 : alpha) * (ctx.globalAlpha || 1);
  ctx.translate(cx, cy); ctx.scale(s, s); ctx.translate(-600, -600);
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.moveTo(170, 370); ctx.lineTo(560, 505); ctx.lineTo(560, 695); ctx.lineTo(170, 830); ctx.lineTo(350, 600); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(1030, 320); ctx.lineTo(640, 505); ctx.lineTo(640, 695); ctx.lineTo(1030, 880); ctx.lineTo(840, 600); ctx.closePath(); ctx.fill();
  roundRect(ctx, 584, 458, 32, 284, 16); ctx.fill();
  ctx.restore();
}

function init(st) {
  st.s.font = st.tk.registerFamily('ForgeLarsen', SERIF, '900') || undefined;
  st.s.pop = 0;
}
function onBeat(st, power) { st.s.pop = Math.min(1, st.s.pop + power * 0.35); }

function background(ctx, st) {
  const { W, H } = st; st.s.pop *= 0.9;
  if (!st.overlayMode) {
    // cream paper with a soft warm vertical wash
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#f1e9db'); g.addColorStop(1, '#e6dccb');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    // subtle paper grain
    ctx.save(); ctx.globalAlpha = 0.04; ctx.globalCompositeOperation = 'multiply'; ctx.drawImage(st.tk.grain(), 0, 0); ctx.restore();
  } else {
    // over footage: a gentle warm scrim low in frame to seat the captions
    const g = ctx.createLinearGradient(0, H * 0.55, 0, H);
    g.addColorStop(0, 'rgba(20,16,10,0)'); g.addColorStop(1, 'rgba(20,16,10,0.42)');
    ctx.fillStyle = g; ctx.fillRect(0, H * 0.55, W, H * 0.45);
  }
}

function paintToken(ctx, o) {
  const { T, cx, cy, px, scl, alpha, emph, st, ghost } = o, tk = st.tk;
  ctx.save(); ctx.globalAlpha = alpha; ctx.translate(cx, cy); ctx.scale(scl, scl);
  ctx.font = tk.font(px, 900, st.s.font); ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  if (ghost) { ctx.fillStyle = 'rgba(243,236,222,0.45)'; ctx.fillText(T, 0, 0); ctx.restore(); return; }
  // defined outline (no box): a thin dark keyline + a clear cream band, then the
  // ink letter. Reads on light footage (dark keyline) AND dark footage (cream band).
  ctx.lineJoin = 'round';
  ctx.shadowColor = 'rgba(20,14,6,0.35)'; ctx.shadowBlur = px * 0.06; ctx.shadowOffsetY = px * 0.02;
  ctx.strokeStyle = 'rgba(26,18,8,0.62)'; ctx.lineWidth = px * 0.26; ctx.strokeText(T, 0, 0);
  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = 'rgba(244,237,223,0.98)'; ctx.lineWidth = px * 0.18; ctx.strokeText(T, 0, 0);
  ctx.fillStyle = emph ? palette.accent : palette.ink;
  ctx.fillText(T, 0, 0);
  if (emph) {
    // one elegant hairline underline in rust
    const w2 = (o.tw || ctx.measureText(T).width) / 2, y = px * 0.42;
    ctx.strokeStyle = palette.accent; ctx.lineWidth = px * 0.028; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(-w2 * 0.92, y); ctx.lineTo(w2 * 0.92, y); ctx.stroke();
  }
  ctx.restore();
}

function drawCue(ctx, st) {
  const anchor = st.mode === 'talk' ? 0.82 : 0.62;
  layoutCue(ctx, st, { sizes: { big: 108, mid: 90, small: 56 }, anchor, safeW: 0.84, reveal: 0.26, stagger: 0.07, trail: 0, trans: 0.26, family: st.s.font, paintToken });
}

function overlay(ctx, st) {
  const tk = st.tk;
  // warm film grade: faint grain + soft vignette
  ctx.save(); ctx.globalAlpha = 0.03; ctx.globalCompositeOperation = 'multiply'; ctx.drawImage(tk.grain(), -Math.random() * 80, -Math.random() * 80); ctx.restore();
  ctx.drawImage(tk.vignette(st.overlayMode ? 0.34 : 0.5, 380), 0, 0);
  // slim rust progress rule
  ctx.fillStyle = palette.accent; ctx.fillRect(0, st.H - 5, st.W * st.progress, 5);
}

module.exports = { id: 'larsen', label: 'Editorial · serif on cream paper', palette, init, background, drawCue, overlay, onBeat, paintToken };
